// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { apiClient, API_ENDPOINTS } from '../../config/api';

/**
 * QA Context Settings Step Component
 * 
 * Step 5 of QA Criteria Creation Wizard
 * Allows users to configure conversation context settings
 */
const QAContextSettingsStep = ({
  formData,
  errors,
  onChange
}) => {
  console.log('🔍 QAContextSettingsStep: Component rendered with props:', { formData, errors, onChange });
  
  const [loading, setLoading] = useState(false);
  const [contextOptions, setContextOptions] = useState({
    conversationTypes: [],
    participants: [],
    targetAudiences: []
  });

  // Debug logging
  useEffect(() => {
    console.log('🔍 QAContextSettingsStep received formData:', formData);
    console.log('🔍 QAContextSettingsStep qa_context:', formData?.qa_context);
    console.log('🔍 QAContextSettingsStep contextOptions:', contextOptions);
  }, [formData, contextOptions]);

  // Load context options from system settings
  useEffect(() => {
    console.log('🔍 QAContextSettingsStep: Component mounted, loading context options...');
    loadContextOptions();
  }, []);

  const loadContextOptions = async () => {
    setLoading(true);
    try {
      console.log('Loading QA context options from:', API_ENDPOINTS.SETTINGS_CATEGORY_QA_CONTEXT);
      console.log('🔍 Full API endpoint URL:', `${apiClient.defaults.baseURL}${API_ENDPOINTS.SETTINGS_CATEGORY_QA_CONTEXT}`);
      const response = await apiClient.get(API_ENDPOINTS.SETTINGS_CATEGORY_QA_CONTEXT);
      console.log('QA context options response:', response.data);
      console.log('🔍 Full response structure:', JSON.stringify(response.data, null, 2));
      
      if (response.data && Array.isArray(response.data)) {
        const settings = response.data;
        console.log('Settings data (array):', settings);
        
        // Convert array to object for easier access
        const settingsObj = {};
        settings.forEach(setting => {
          settingsObj[setting.setting_key] = setting;
        });
        console.log('Settings object:', settingsObj);
        
        const options = {
          conversationTypes: settingsObj.conversation_types?.setting_value || [],
          participants: settingsObj.participant_types?.setting_value || [],
          targetAudiences: settingsObj.target_audiences?.setting_value || []
        };
        
        console.log('Parsed options:', options);
        console.log('🔍 Setting contextOptions with:', options);
        setContextOptions(options);
      } else {
        console.log('No settings found, using fallback options');
        // Fallback options if API doesn't return data
        setContextOptions({
          conversationTypes: [
            { label: 'Booking/Reservation', value: 'booking' },
            { label: 'Customer Support', value: 'support' },
            { label: 'Sales Inquiry', value: 'sales' },
            { label: 'Technical Support', value: 'technical' }
          ],
          participants: [
            { label: 'Agent ↔ Customer', value: 'agent-customer' },
            { label: 'Agent ↔ Manager', value: 'agent-manager' },
            { label: 'Customer ↔ System', value: 'customer-system' }
          ],
          targetAudiences: [
            { label: 'VIP Customers', value: 'vip-customers' },
            { label: 'Regular Customers', value: 'regular-customers' },
            { label: 'New Customers', value: 'new-customers' },
            { label: 'Corporate Clients', value: 'corporate-clients' }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to load QA context options:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Fallback options on error
      setContextOptions({
        conversationTypes: [
          { label: 'Booking/Reservation', value: 'booking' },
          { label: 'Customer Support', value: 'support' },
          { label: 'Sales Inquiry', value: 'sales' },
          { label: 'Technical Support', value: 'technical' }
        ],
        participants: [
          { label: 'Agent ↔ Customer', value: 'agent-customer' },
          { label: 'Agent ↔ Manager', value: 'agent-manager' },
          { label: 'Customer ↔ System', value: 'customer-system' }
        ],
        targetAudiences: [
          { label: 'VIP Customers', value: 'vip-customers' },
          { label: 'Regular Customers', value: 'regular-customers' },
          { label: 'New Customers', value: 'new-customers' },
          { label: 'Corporate Clients', value: 'corporate-clients' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    onChange(field, value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading context options...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        QA Context Settings
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure the conversation context that will be used for AI analysis. 
        This helps the AI understand the type of conversation and provide more accurate evaluations.
      </Typography>

      {errors.qaContext && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.qaContext}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Conversation Type */}
        <FormControl fullWidth error={!!errors.conversation_type}>
          <InputLabel>Conversation Type</InputLabel>
          <Select
            value={formData.qa_context?.conversation_type || ''}
            onChange={(e) => {
              console.log('🔍 Conversation Type changed to:', e.target.value);
              handleChange('qa_context', {
                ...formData.qa_context,
                conversation_type: e.target.value
              });
            }}
            label="Conversation Type"
          >
            {console.log('🔍 Conversation Type Select value:', formData.qa_context?.conversation_type || '')}
            {console.log('🔍 Conversation Type options:', contextOptions.conversationTypes)}
            {contextOptions.conversationTypes.map((type) => (
              <MenuItem key={type.value || type} value={type.value || type}>
                {type.label || type}
              </MenuItem>
            ))}
          </Select>
          {errors.conversation_type && (
            <FormHelperText>{errors.conversation_type}</FormHelperText>
          )}
        </FormControl>

        {/* Participants */}
        <FormControl fullWidth error={!!errors.participants}>
          <InputLabel>Participants</InputLabel>
          <Select
            value={formData.qa_context?.participants || ''}
            onChange={(e) => handleChange('qa_context', {
              ...formData.qa_context,
              participants: e.target.value
            })}
            label="Participants"
          >
            {console.log('🔍 Participants options:', contextOptions.participants)}
            {contextOptions.participants.map((participant) => (
              <MenuItem key={participant.value || participant} value={participant.value || participant}>
                {participant.label || participant}
              </MenuItem>
            ))}
          </Select>
          {errors.participants && (
            <FormHelperText>{errors.participants}</FormHelperText>
          )}
        </FormControl>

        {/* Target Audience */}
        <FormControl fullWidth error={!!errors.target_audience}>
          <InputLabel>Target Audience</InputLabel>
          <Select
            value={formData.qa_context?.target_audience || ''}
            onChange={(e) => handleChange('qa_context', {
              ...formData.qa_context,
              target_audience: e.target.value
            })}
            label="Target Audience"
          >
            {console.log('🔍 Target Audience options:', contextOptions.targetAudiences)}
            {contextOptions.targetAudiences.map((audience) => (
              <MenuItem key={audience.value || audience} value={audience.value || audience}>
                {audience.label || audience}
              </MenuItem>
            ))}
          </Select>
          {errors.target_audience && (
            <FormHelperText>{errors.target_audience}</FormHelperText>
          )}
        </FormControl>

        {/* Conversation Objective */}
        <TextField
          fullWidth
          label="Conversation Objective"
          placeholder="What should be achieved in this conversation?"
          value={formData.qa_context?.conversation_objective || ''}
          onChange={(e) => handleChange('qa_context', {
            ...formData.qa_context,
            conversation_objective: e.target.value
          })}
          error={!!errors.conversation_objective}
          helperText={errors.conversation_objective || "Describe the main goal or purpose of the conversation"}
          multiline
          rows={2}
        />

        {/* Ideal Approach */}
        <TextField
          fullWidth
          label="Ideal Approach"
          placeholder="How should the conversation be conducted?"
          value={formData.qa_context?.ideal_approach || ''}
          onChange={(e) => handleChange('qa_context', {
            ...formData.qa_context,
            ideal_approach: e.target.value
          })}
          error={!!errors.ideal_approach}
          helperText={errors.ideal_approach || "Describe the preferred approach or methodology"}
          multiline
          rows={2}
        />

        {/* Context Notes */}
        <TextField
          fullWidth
          label="Additional Context Notes"
          placeholder="Any additional context or special considerations?"
          value={formData.qa_context?.context_notes || ''}
          onChange={(e) => handleChange('qa_context', {
            ...formData.qa_context,
            context_notes: e.target.value
          })}
          error={!!errors.context_notes}
          helperText={errors.context_notes || "Optional: Additional context that might be relevant"}
          multiline
          rows={3}
        />
      </Box>
    </Box>
  );
};

export default QAContextSettingsStep;
